/**
 * Configurazione versionata delle protezioni anti-bot in registrazione.
 *
 * Sorgente: tabella `signup_protection_config` (versione attiva) letta via RPC
 * `get_signup_protection_config`. Se la lettura fallisce si usano i default,
 * identici ai valori storicamente hardcoded.
 */
import { supabase } from '@/integrations/supabase/client';

export interface SignupProtectionConfig {
  recaptcha: {
    min_score_user_signup: number;
    min_score_business_registration: number;
    block_when_score_missing: boolean;
  };
  rate_limit: {
    max_per_hour: number;
    max_per_day: number;
  };
  email: {
    block_undeliverable_domains: boolean;
    block_disposable_domains: boolean;
    block_bot_patterns: boolean;
    extra_blocked_domains: string[];
    extra_disposable_tokens: string[];
    extra_bot_patterns: string[];
  };
}

export interface SignupProtectionVersion {
  id: string;
  version: number;
  is_active: boolean;
  config: SignupProtectionConfig;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export const DEFAULT_SIGNUP_PROTECTION_CONFIG: SignupProtectionConfig = {
  recaptcha: {
    min_score_user_signup: 0.5,
    min_score_business_registration: 0.3,
    block_when_score_missing: false,
  },
  rate_limit: {
    max_per_hour: 3,
    max_per_day: 10,
  },
  email: {
    block_undeliverable_domains: true,
    block_disposable_domains: true,
    block_bot_patterns: true,
    extra_blocked_domains: [],
    extra_disposable_tokens: [],
    extra_bot_patterns: [],
  },
};

const asNumber = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const asBool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

const asStringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : [];

/** Normalizza un oggetto sconosciuto in una config completa e sicura */
export function normalizeSignupProtectionConfig(raw: unknown): SignupProtectionConfig {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  const d = DEFAULT_SIGNUP_PROTECTION_CONFIG;
  const r = src.recaptcha || {};
  const rl = src.rate_limit || {};
  const em = src.email || {};

  return {
    recaptcha: {
      min_score_user_signup: asNumber(r.min_score_user_signup, d.recaptcha.min_score_user_signup),
      min_score_business_registration: asNumber(
        r.min_score_business_registration,
        d.recaptcha.min_score_business_registration
      ),
      block_when_score_missing: asBool(
        r.block_when_score_missing,
        d.recaptcha.block_when_score_missing
      ),
    },
    rate_limit: {
      max_per_hour: Math.max(1, Math.round(asNumber(rl.max_per_hour, d.rate_limit.max_per_hour))),
      max_per_day: Math.max(1, Math.round(asNumber(rl.max_per_day, d.rate_limit.max_per_day))),
    },
    email: {
      block_undeliverable_domains: asBool(
        em.block_undeliverable_domains,
        d.email.block_undeliverable_domains
      ),
      block_disposable_domains: asBool(
        em.block_disposable_domains,
        d.email.block_disposable_domains
      ),
      block_bot_patterns: asBool(em.block_bot_patterns, d.email.block_bot_patterns),
      extra_blocked_domains: asStringList(em.extra_blocked_domains).map((s) => s.toLowerCase()),
      extra_disposable_tokens: asStringList(em.extra_disposable_tokens).map((s) => s.toLowerCase()),
      extra_bot_patterns: asStringList(em.extra_bot_patterns),
    },
  };
}

export interface ConfigValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/** Validazione lato UI prima del salvataggio */
export function validateSignupProtectionConfig(
  config: SignupProtectionConfig
): ConfigValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const { recaptcha, rate_limit, email } = config;

  const scores: [string, number][] = [
    ['Soglia registrazione utente', recaptcha.min_score_user_signup],
    ['Soglia registrazione attività', recaptcha.min_score_business_registration],
  ];
  for (const [label, value] of scores) {
    if (!Number.isFinite(value) || value < 0 || value > 1) {
      errors.push(`${label}: deve essere un valore tra 0 e 1.`);
    } else if (value > 0.7) {
      warnings.push(`${label} è ${value}: rischio elevato di falsi positivi.`);
    }
  }

  if (!Number.isInteger(rate_limit.max_per_hour) || rate_limit.max_per_hour < 1) {
    errors.push('Tentativi per ora: deve essere un numero intero ≥ 1.');
  }
  if (!Number.isInteger(rate_limit.max_per_day) || rate_limit.max_per_day < 1) {
    errors.push('Tentativi per giorno: deve essere un numero intero ≥ 1.');
  }
  if (
    Number.isInteger(rate_limit.max_per_hour) &&
    Number.isInteger(rate_limit.max_per_day) &&
    rate_limit.max_per_hour > rate_limit.max_per_day
  ) {
    errors.push('Il limite orario non può superare il limite giornaliero.');
  }

  for (const pattern of email.extra_bot_patterns) {
    try {
      new RegExp(pattern, 'i');
    } catch {
      errors.push(`Pattern non valido: "${pattern}".`);
    }
  }

  for (const domain of email.extra_blocked_domains) {
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)) {
      errors.push(`Dominio non valido: "${domain}".`);
    }
  }

  if (
    !email.block_undeliverable_domains &&
    !email.block_disposable_domains &&
    !email.block_bot_patterns
  ) {
    warnings.push('Tutti i controlli email sono disattivati: nessun filtro sarà applicato.');
  }

  return { valid: errors.length === 0, errors, warnings };
}

/** Soglia reCAPTCHA per flusso */
export function minScoreForFlow(
  config: SignupProtectionConfig,
  flow: 'user_signup' | 'business_registration'
): number {
  return flow === 'business_registration'
    ? config.recaptcha.min_score_business_registration
    : config.recaptcha.min_score_user_signup;
}

// --- Cache client (60s) ---------------------------------------------------

let cached: { config: SignupProtectionConfig; version: number; at: number } | null = null;
let inflight: Promise<{ config: SignupProtectionConfig; version: number }> | null = null;
const TTL_MS = 60_000;

/**
 * Config attiva con cache in memoria. Mai lancia: in caso di errore ritorna i default,
 * mantenendo il comportamento storico.
 */
export async function getSignupProtectionConfigCached(): Promise<{
  config: SignupProtectionConfig;
  version: number;
}> {
  if (cached && Date.now() - cached.at < TTL_MS) {
    return { config: cached.config, version: cached.version };
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_signup_protection_config' as any);
      if (error || !data) throw error || new Error('empty config');
      const payload = data as any;
      const config = normalizeSignupProtectionConfig(payload.config);
      const version = Number(payload.version) || 0;
      cached = { config, version, at: Date.now() };
      return { config, version };
    } catch {
      return { config: DEFAULT_SIGNUP_PROTECTION_CONFIG, version: 0 };
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Opzioni per `checkSignupEmail` derivate dalla configurazione attiva.
 * Non lancia mai: in caso di errore usa i default.
 */
export async function getEmailGuardOptionsCached(): Promise<
  SignupProtectionConfig['email']
> {
  try {
    const { config } = await getSignupProtectionConfigCached();
    return config.email;
  } catch {
    return DEFAULT_SIGNUP_PROTECTION_CONFIG.email;
  }
}

/** Invalida la cache (usato dopo un salvataggio admin) */
export function invalidateSignupProtectionConfigCache(): void {
  cached = null;
}
